import { InfoOutlined, Launch } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Tooltip,
  styled,
  tooltipClasses,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Stack } from "@mui/system";
import * as React from "react";
import { convertDate, convertToINR } from "../../../helpers/Utils";
import { ApprovalStatus, _STATUS } from "../constants/statusMapping";
import OrderStatusBadge from "./OrderStatusBadge";
import Checkbox from "../../../components/Checkbox";
import { COLOR_CONSTANTS } from "../../../helpers/Colors";

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    fontSize: "12px",
  },
}));

function removeUnderScoreAndCapitalize(str) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Component to show PO details and show/collapse the nested invoices
function InvoiceRow(props) {
  const { row, selectInvoice, selectCreditNote, setBulkSelected, bulkSelected } = props;
  const isSelected = bulkSelected.indexOf(row.id) > -1;
  const { validation_result, system_validation_status } = row;

  const { parser_invoice_po_result } = validation_result || {};

  const validation_data = parser_invoice_po_result?.validation_field_results;

  const unmatchedFields = Object.keys(validation_data || {}).filter((field) => {
    return !validation_data?.[field]?.match_status;
  });

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": {
            backgroundColor:
              bulkSelected.indexOf(row.id) > -1
                ? COLOR_CONSTANTS["primary/variant"]
                : "",
          },
        }}
      >
        <TableCell>
          <Checkbox
            // title={parser_invoice_po_result.match_status ? "Select" : "Not applicable"}
            checked={isSelected}
            disabled={!parser_invoice_po_result?.match_status}
            onChange={(e) => {
              if (e.target.checked) {
                setBulkSelected((prev) => [...prev, row.id]);
              } else {
                setBulkSelected((prev) => prev.filter((id) => id !== row.id));
              }
            }}
          />
        </TableCell>
        <TableCell>{row.invoice_no}</TableCell>
        <TableCell>{row.po_code}</TableCell>
        <TableCell>{row.vendor_name}</TableCell>
        <TableCell>{convertDate(row.invoice_date)}</TableCell>
        <TableCell align="right">
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            gap={1}
          >
            {convertToINR(row.invoice_value)}
            {row.utr ? (
              <CustomTooltip title={row.utr}>
                <InfoOutlined />
              </CustomTooltip>
            ) : null}
          </Stack>
        </TableCell>
        <TableCell>{convertToINR(row.additional_charges)}</TableCell>
        <TableCell>{row.irn_invoice ? "Yes" : "No"}</TableCell>
        <TableCell>{row.grn_id || "-"}</TableCell>
        <TableCell>{convertToINR(row.grn_value)}</TableCell>
        <TableCell>{convertDate(row.grn_date)}</TableCell>
        <TableCell>
          {row.credit_notes?.[0] ? (
            <Button
              disableRipple
              onClick={() =>
                selectCreditNote({
                  creditNote: row.credit_notes[0],
                  po_code: row.po_code,
                  po_id: row.po_id ?? row.metadata?.po_id,
                })
              }
              variant="text"
              style={{
                textDecoration: "underline",
                color: "black",
                padding: 0,
                minWidth: 0,
              }}
            >
              {row.credit_notes[0].number}
            </Button>
          ) : "-"}
        </TableCell>
        <TableCell>
          {row.invoice_status?.state != "approved" ? (
            <Box
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-between"}
              alignItems={"start"}
            >
              <Button
                disableRipple
                onClick={() => selectInvoice(row)}
                variant="text"
                style={{
                  textDecoration: "underline",
                  color: "black",
                  padding: 0,
                  minWidth: 0,
                }}
                endIcon={<Launch />}
              >
                Review
              </Button>

              {system_validation_status !== null && parser_invoice_po_result?.match_percentage >= 0 ? (
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  flexDirection={"row"}
                  gap={"2px"}
                >
                  <span
                    className="ft-12"
                    style={{
                      color: parser_invoice_po_result?.match_status
                        ? COLOR_CONSTANTS["tertiary-green/variant2"]
                        : COLOR_CONSTANTS["tertiary-red/default"],
                      textWrap: "nowrap",
                    }}
                  >
                    {Math.trunc(parser_invoice_po_result?.match_percentage)}%
                    fields match
                  </span>
                  {!parser_invoice_po_result?.match_status ? (
                    <CustomTooltip
                      title={
                        <div style={{ padding: 8 }}>
                          <span className="ft-14">{`Unmatched fields(${unmatchedFields?.length}):`}</span>
                          <ol
                            style={{
                              padding: "10px 0",
                              listStylePosition: "inside",
                              margin: 0,
                            }}
                          >
                            {unmatchedFields.map((field) => (
                              <li style={{ marginBottom: 4 }} className="ft-14">
                                {removeUnderScoreAndCapitalize(field)}
                              </li>
                            ))}
                          </ol>
                        </div>
                      }
                    >
                      <InfoOutlined />
                    </CustomTooltip>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ) : (
            <Box padding={"6px"}>
              <a
                href={row.invoice_image.pre_signed_url}
                target="_blank"
                rel="noreferrer"
              >
                {row.invoice_image.file_name || "Invoice Link"}
              </a>
            </Box>
          )}
        </TableCell>
        <TableCell>
          <OrderStatusBadge
            type={ApprovalStatus[row.invoice_status?.state]?.code}
            label={row.invoice_status.label}
          />
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function InvoiceTable({ data, selectInvoice, selectCreditNote, bulkSelection }) {
  const [bulkSelected, setBulkSelected] = bulkSelection;

  return (
    <TableContainer
      component={Card}
      sx={{ boxShadow: "none", overflowX: "scroll" }}
    >
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow sx={{ borderBottom: "2px solid #E6E6E6" }}>
            <TableCell>
              <Checkbox
                checked={bulkSelected.length === data.length}
                disabled={
                  data.filter(
                    (row) =>
                      row.validation_result?.parser_invoice_po_result
                        ?.match_status
                  )?.length === 0
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setBulkSelected(
                      data
                        .filter(
                          (row) =>
                            row.validation_result?.parser_invoice_po_result
                              ?.match_status
                        )
                        ?.map((row) => row.id)
                    );
                  } else {
                    setBulkSelected([]);
                  }
                }}
              />
            </TableCell>
            <TableCell>Invoice No</TableCell>
            <TableCell>PO ID</TableCell>
            <TableCell>Vendor Name</TableCell>
            <TableCell>Invoice Date</TableCell>
            <TableCell align="right">Invoice Value</TableCell>
            <TableCell>Additional Charges</TableCell>
            <TableCell>IRN Invoice</TableCell>
            <TableCell>GRN ID</TableCell>
            <TableCell>GRN Value</TableCell>
            <TableCell>GRN Date</TableCell>
            <TableCell>Credit Note</TableCell>
            <TableCell>Invoice</TableCell>
            <TableCell>Invoice Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <InvoiceRow
              key={row.id}
              row={row}
              selectInvoice={selectInvoice}
              selectCreditNote={selectCreditNote}
              setBulkSelected={setBulkSelected}
              bulkSelected={bulkSelected}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
